import { ExtractErrorInfo } from "components/shared/ExtractErrorInfo";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { api } from "Config/Api";
import { API_ENDPOINTS } from "Config/apiConfig";
import { AppType } from "types/appType";
import { RepairerType } from "types/RepairerType";
import { ErrorType } from "types/errorType";
import { RootState } from "store/store";
import { ApiResponse } from "utils/fetchDataUtils";
import extractIdFromLocation from "utils/extractIdFromLocation";

export interface AppTypeWithRepairerType extends AppType {
  loading: boolean;
  error: ErrorType | null;
  repairers: RepairerType[];
  minSearchLength: number;
}

const initialState: AppTypeWithRepairerType = {
  error: null,
  loading: false,
  repairers: [],
  minSearchLength: 3,
};

interface repairerProps {
  userId: string;
  organisationId: string;
  searchValue: string;
}

export const fetchRepairers = createAsyncThunk<RepairerType[], repairerProps>(
  "repairers/fetchRepairers",
  async ({ userId, organisationId, searchValue }, { rejectWithValue }) => {
    try {
      const { data } = await api.get<ApiResponse<RepairerType[]>>(API_ENDPOINTS.repairers, {
        params: {
          UserId: userId,
          organisationId: organisationId,
          Search: searchValue,
        },
      });
      return data.results.flat();
    } catch (error: any) {
      const errorInfo = ExtractErrorInfo(error);
      return rejectWithValue(errorInfo);
    }
  }
);

export const addRepairer = createAsyncThunk<RepairerType, { incidentId: string; repairer: RepairerType }>(
  "repairers/addRepairer",
  async ({ incidentId, repairer }, { rejectWithValue }) => {
    try {
      const response = await api.post<RepairerType>(API_ENDPOINTS.incidentRepairers(incidentId), repairer);

      const locationHeader = response.headers["location"] || response.headers["Location"];
      if (!locationHeader) throw new Error("Missing Location header from addRepairer response");

      return {
        ...repairer,
        id: extractIdFromLocation(locationHeader),
      } as RepairerType;
    } catch (error: any) {
      return rejectWithValue(ExtractErrorInfo(error));
    }
  }
);

export const deleteRepairer = createAsyncThunk<string, { incidentId: string; repairerId: string }>(
  "repairers/deleteRepairer",
  async ({ incidentId, repairerId }, { rejectWithValue }) => {
    try {
      await api.delete(API_ENDPOINTS.deleteIncidentRepairer(incidentId, repairerId));
      return repairerId;
    } catch (error: any) {
      return rejectWithValue(ExtractErrorInfo(error));
    }
  }
);

const repairerSlice = createSlice({
  name: "repairers",
  initialState,
  reducers: {
    setRepairer: (state, action) => {
      state.repairers = action.payload;
    },
    clearRepairerError: (state) => {
      return {
        ...state,
        error: null,
      };
    },
    clearRepairer: (state) => {
      state.repairers = [];
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchRepairers.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchRepairers.fulfilled, (state, action) => {
      state.loading = false;
      state.repairers = action.payload;
    });
    builder.addCase(fetchRepairers.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as ErrorType;
    });
    // Handle addRepairer
    builder.addCase(addRepairer.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(addRepairer.fulfilled, (state, action) => {
      state.loading = false;
    });
    builder.addCase(addRepairer.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as ErrorType;
    });
    // Handle deleteRepairer
    builder.addCase(deleteRepairer.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(deleteRepairer.fulfilled, (state, action) => {
      state.loading = false;
    });
    builder.addCase(deleteRepairer.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as ErrorType;
    });
  },
});

export const { clearRepairer, clearRepairerError } = repairerSlice.actions;
export const actionRootState = (state: RootState) => state;
export default repairerSlice.reducer;
